/**
 * Extract a human-readable error message from a backend API error response.
 * Handles the various formats Django REST Framework returns.
 *
 * Usage (axios):
 *   import { extractErrorMessage } from "../utils/errorUtils";
 *   catch (err) {
 *     const msg = extractErrorMessage(err.response?.data, "Something went wrong");
 *   }
 */

/**
 * Extract an error message from backend response data.
 * @param {any} data - The response body (err.response.data)
 * @param {string} fallback - Fallback message if nothing can be extracted
 * @returns {string}
 */
export const extractErrorMessage = (data, fallback = "Something went wrong. Please try again.") => {
  if (!data) return fallback;

  // Plain string
  if (typeof data === "string") return data || fallback;

  // Array of messages
  if (Array.isArray(data)) {
    return data[0] || fallback;
  }

  // DRF validation failed with field_errors
  if (data.error === "Validation failed" && data.field_errors) {
    for (const [field, errors] of Object.entries(data.field_errors)) {
      if (Array.isArray(errors) && errors.length > 0) {
        const fieldName = field === "phone_number" ? "Phone number" : field;
        return `${fieldName}: ${errors[0]}`;
      }
    }
  }

  // errors: string
  if (data.errors && typeof data.errors === "string") {
    return data.errors;
  }

  // errors: array
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  // errors: object
  if (data.errors && typeof data.errors === "object") {
    if (Array.isArray(data.errors.non_field_errors)) {
      return data.errors.non_field_errors[0] || fallback;
    }
    for (const key of ["email", "password", "phone_number", "otp", "confirm_password"]) {
      if (Array.isArray(data.errors[key]) && data.errors[key].length > 0) {
        return data.errors[key][0];
      }
    }
  }

  // non_field_errors at top level
  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
    return data.non_field_errors[0];
  }

  // DRF standard fields
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  if (data.message) return data.message;

  // Field-specific arrays at top level
  for (const key of ["email", "password", "phone_number", "otp", "confirm_password"]) {
    if (Array.isArray(data[key]) && data[key].length > 0) {
      return data[key][0];
    }
  }

  // Generic field error fallback — first array field found
  for (const [field, errors] of Object.entries(data)) {
    if (Array.isArray(errors) && errors.length > 0) {
      const fieldName = field
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return `${fieldName}: ${errors[0]}`;
    }
  }

  return fallback;
};

/**
 * Extract an error message directly from an axios error object.
 * @param {any} err - The caught axios error
 * @param {string} fallback - Fallback message
 * @returns {string}
 */
export const extractAxiosError = (err, fallback = "Something went wrong. Please try again.") => {
  if (!err) return fallback;

  if (err.response?.data) {
    return extractErrorMessage(err.response.data, fallback);
  }

  // Network error (no response)
  if (err.request) {
    return "No response from server. Please check your connection.";
  }

  // Request setup error
  if (err.message) {
    return err.message;
  }

  return fallback;
};
